<template>
  <div v-on-click-outside="() => (showMenu = false)" class="relative">
    <primary-button
      class="h-10"
      :disabled="disabled"
      @click="handleButtonClick"
    >
      <span class="mr-6">
        {{ $t("ORCHESTRATION.TRIGGER") }}
        {{ $t("ORGANIZATION.API_CONFIG.WEBHOOKS", 2) }}
      </span>
    </primary-button>

    <div
      data-cy="webhooks-dropdown"
      class="absolute top-0 right-0 flex h-10 w-10 transform cursor-pointer items-center justify-center rounded-r transition-all"
      :class="{
        'bg-blue-800': showMenu,
        'pointer-events-none cursor-not-allowed': disabled,
      }"
      @click="showMenu = !showMenu"
    >
      <icon-base
        class="translate-x-0.5 translate-y-1 transform text-white"
        :icon="IconArrowUp"
      />
    </div>

    <trigger-webhooks-menu
      v-if="showMenu"
      ref="webhooksMenuRef"
      :show="showMenu"
      :webhooks="customWebhooksWithParams"
      :checked-items="checkedItems"
      :filters="filters"
      :triggering="triggeringCustomWebhooks"
      @search:update="filters.search = $event"
      @webhook:toggle="handleWebhookToggle"
      @parameter:change="handleParameterChange"
    />
  </div>
</template>

<script setup lang="ts">
import { type PropType, computed, onUnmounted, ref, watch } from "vue";

import cloneDeep from "lodash/cloneDeep";
import compact from "lodash/compact";

import { useCustomWebhooks } from "@/hooks/customWebhooks";
import { useCustomWebhookStore } from "@/stores/customWebhook";

import IconArrowUp from "@/components/icons/IconArrowUp.vue";
import TriggerWebhooksMenu from "@/views/deals/components/controlPanel/TriggerWebhooksMenu.vue";

import type { OptionalWorkflowWebhookParameter } from "@/models/workflows";

const { applicationIds } = defineProps({
  applicationIds: {
    type: Array as PropType<string[]>,
    required: true,
  },
});

const store = useCustomWebhookStore();
const showMenu = ref(false);
const checkedItems = ref<number[]>([]);
const webhooksMenuRef = ref<typeof TriggerWebhooksMenu | null>(null);

const disabled = computed(
  () =>
    !applicationIds.length ||
    loadingCustomWebhooks.value ||
    triggeringCustomWebhooks.value
);

const {
  filters,
  customWebhooks,
  loadingCustomWebhooks,
  triggerCustomWebhooks,
  triggeringCustomWebhooks,
} = useCustomWebhooks();

const customWebhooksWithParams = ref(
  cloneDeep(customWebhooks.value.data || [])
);

const handleWebhookToggle = (id: number | string) => {
  const index = checkedItems.value.indexOf(Number(id));
  index > -1
    ? checkedItems.value.splice(index, 1)
    : checkedItems.value.push(Number(id));
};

const handleButtonClick = async () => {
  if (disabled.value) {
    return;
  }

  const payload = compact(
    checkedItems.value.map((id) => {
      const webhook = customWebhooksWithParams.value.find(
        (webhook) => webhook.id === id
      );
      if (!webhook) {
        return;
      }
      return {
        id,
        parameters:
          webhook.parameters?.map(({ name, value }) => ({
            name,
            value,
          })) || [],
      };
    })
  );

  await triggerCustomWebhooks(applicationIds, payload);
  checkedItems.value = [];
  showMenu.value = !showMenu.value;
};

const handleParameterChange = (
  id: number | string,
  parameters: OptionalWorkflowWebhookParameter[]
) => {
  const webhook = customWebhooksWithParams.value.find(
    (webhook) => webhook.id === id
  );
  if (!webhook) {
    return;
  }
  webhook.parameters = parameters;
};

watch(
  () => customWebhooks.value.data,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      checkedItems.value = [];
      customWebhooksWithParams.value = cloneDeep(
        customWebhooks.value.data || []
      );
    }
  }
);

watch(
  () => applicationIds,
  () => {
    if (!applicationIds.length) {
      checkedItems.value = [];
    }
  },
  { deep: true }
);

onUnmounted(() => {
  store.unsetCustomWebhooks();
});
</script>
